import { useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { type ChatFile, downloadChatFile, formatFileSize, isRenderableImage } from '@/lib/chat/chatFiles';
import { useSignedChatFileUrl } from './hooks/use-signed-chat-file-url';

type ChatAttachmentProps = {
    file: ChatFile;
    isOwnMessage: boolean;
    /** Called once an inline image has loaded and taken up its final space. */
    onImageLoad?: () => void;
};

/** Images render inline (click to enlarge); everything else is a download chip. */
export function ChatAttachment({ file, isOwnMessage, onImageLoad }: ChatAttachmentProps) {
    if (isRenderableImage(file)) {
        return <ChatImageAttachment file={file} isOwnMessage={isOwnMessage} onImageLoad={onImageLoad} />;
    }
    return <ChatFileAttachment file={file} isOwnMessage={isOwnMessage} />;
}

function ChatImageAttachment({ file, isOwnMessage, onImageLoad }: ChatAttachmentProps) {
    const url = useSignedChatFileUrl(file.path);
    const [failed, setFailed] = useState(false);
    const [open, setOpen] = useState(false);

    // Signing failed or the browser could not decode it: still let the user get the file.
    if (url === null || failed) {
        return <ChatFileAttachment file={file} isOwnMessage={isOwnMessage} />;
    }

    if (url === undefined) {
        return (
            <div
                className="h-40 w-52 max-w-full rounded-lg bg-foreground/10 animate-pulse"
                role="img"
                aria-label={file.name}
            />
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                className="block w-fit max-w-full rounded-lg overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label={`Visa ${file.name} i full storlek`}
            >
                <img
                    src={url}
                    alt={file.name}
                    className="block max-h-72 max-w-full object-contain"
                    onLoad={onImageLoad}
                    onError={() => setFailed(true)}
                />
            </button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    aria-describedby={undefined}
                    className="w-fit max-w-[95vw] p-0 border-none bg-transparent shadow-none [&>button]:rounded-full [&>button]:bg-background/80 [&>button]:p-1 [&>button]:text-foreground [&>button]:opacity-100"
                >
                    <VisuallyHidden>
                        <DialogTitle>{file.name}</DialogTitle>
                    </VisuallyHidden>
                    <img
                        src={url}
                        alt={file.name}
                        className="block max-h-[90dvh] max-w-[95vw] object-contain rounded-lg"
                    />
                </DialogContent>
            </Dialog>
        </>
    );
}

function ChatFileAttachment({ file, isOwnMessage }: Pick<ChatAttachmentProps, 'file' | 'isOwnMessage'>) {
    const { toast } = useToast();
    const [downloading, setDownloading] = useState(false);
    const size = formatFileSize(file.size);

    const handleDownload = async () => {
        if (downloading) return;
        setDownloading(true);
        try {
            await downloadChatFile(file);
        } catch (error) {
            console.error(error);
            toast({
                title: 'Kunde inte ladda ner filen',
                description: 'Försök igen.',
                variant: 'destructive',
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className={cn(
                'flex items-center gap-2 rounded-lg px-2 py-1.5 text-left max-w-full transition-colors',
                isOwnMessage
                    ? 'bg-primary-foreground/15 hover:bg-primary-foreground/25'
                    : 'bg-background/60 hover:bg-background'
            )}
            aria-label={`Ladda ner ${file.name}`}
        >
            <FileText className="size-5 shrink-0" />
            <span className="flex flex-col min-w-0">
                <span className="truncate font-medium">{file.name}</span>
                {size && <span className="text-xs opacity-70">{size}</span>}
            </span>
            {downloading
                ? <Loader2 className="size-4 shrink-0 animate-spin" />
                : <Download className="size-4 shrink-0 opacity-70" />}
        </button>
    );
}
